// The office's invoice sheet (rs-office1's walk): one finalized invoice, opened from wherever it's
// named (a customer's Money tab and History, Lists, its #/doc/invoice link): its number and status,
// when it was issued and is due, its PO, lines, tax and totals, and what settled it. From here it's
// sent again, copied as a link or saved as a PDF, and the owner writes off what's owed. A draft is
// still edited at Review. One from ServiceTitan says so while the company mirrors it, and what a credit
// note took off it reads as an adjustment, never as money paid (history fix 2). A refund is a credit
// note too: the invoice stays paid, and its total, tax and what was paid all read less it (QA-713).
// This is Riverstone's sheet; Blueprint draws InvoiceLayer.tsx, on the same reads (invoice-state.ts).

import { useEffect, useRef, useState } from 'react';
import '@flowology/ui-web/base.css';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { todayIn } from '../books/model.ts';
import { Money } from '../books/ui.tsx';
import { CardBeingChecked, WriteOff } from '../history/MoneyActions.tsx';
import { methodWord, moneyRights } from '../history/money-model.ts';
import { openJob } from '../job/job-page.ts';
import { lineTagsText } from '../job/job-model.ts';
import { InvoiceActions } from '../review/InvoiceActions.tsx';
import { RecordPayment } from '../books/RecordPayment.tsx';
import { Loading } from '../shared/Loading.tsx';
import { closeSlot, onLeave, openSlot, type Slot } from '../shared/sheet-slot.ts';
import { SignedForWork } from '../shared/SignedForWork.tsx';
import { dateText } from '@flowology/kernel';
import { useDesign } from '../blueprint/design.ts';
import {
  adjustedWord,
  afterRefunds,
  appliedWord,
  draftNote,
  dueText,
  qtyText,
  rateText,
  refundTaxText,
  restOfPayment,
  statusOf,
  type InvoiceSheetData,
} from './invoice-model.ts';
import { InvoiceLayer } from './InvoiceLayer.tsx';
import { useInvoice } from './invoice-state.ts';
import '../job/job.css';
import './invoice-sheet.css';
import { answerOpenInvoice } from './open-invoice.ts';

export { openInvoice } from './open-invoice.ts';

/** Mounted once by the app: holds the sheet and answers openInvoice. */
export function InvoiceSheetHost({
  held,
  timeZone,
  onOpenCustomer,
  onChanged,
}: {
  held: readonly string[];
  timeZone: string;
  /** The customer's record, on their Money tab with this invoice's row in view. */
  onOpenCustomer: (customerId: string, invoiceId: string) => void;
  /** Money moved on it (a write-off, a payment): places showing it may re-read. */
  onChanged?: () => void;
}) {
  // Each opening is its own (`at`): a sheet closing late never closes the one opened after it.
  const design = useDesign();
  const [open, setOpen] = useState<Slot<string> | null>(null);
  useEffect(() => {
    answerOpenInvoice((id) => setOpen(openSlot(id)));
    // leaving the page it's over (Back, a link) closes it: the view follows the address
    const stop = onLeave(window, () => setOpen(null));
    return () => {
      answerOpenInvoice(null);
      stop();
    };
  }, []);
  if (!open) return null;
  const { value: id, at } = open;
  const shut = () => setOpen((cur) => closeSlot(cur, at));
  // Blueprint's is on ui-web's dialog layer, drawn with catalogued parts (InvoiceLayer.tsx, BL-189)
  const Sheet = design === 'blueprint' ? InvoiceLayer : InvoiceSheet;
  return (
    <Sheet
      key={`${id}-${at}`}
      invoiceId={id}
      held={held}
      timeZone={timeZone}
      onClose={shut}
      onOpenCustomer={(c) => {
        shut();
        onOpenCustomer(c, id);
      }}
      onChanged={onChanged}
    />
  );
}

function InvoiceSheet({
  invoiceId,
  held,
  timeZone,
  onClose,
  onOpenCustomer,
  onChanged,
}: {
  invoiceId: string;
  held: readonly string[];
  timeZone: string;
  onClose: () => void;
  onOpenCustomer: (customerId: string) => void;
  onChanged?: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { inv, refunds, depositNotes, error, said, setSaid, moved } = useInvoice(invoiceId, onChanged);
  const [writingOff, setWritingOff] = useState(false);
  const [paying, setPaying] = useState(false);
  // the invoice and its payments are ServiceTitan's while the company mirrors it (BL-088)
  const mirroring = useMirror();
  const rights = moneyRights(held, mirroring);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const today = todayIn(timeZone);
  // a local date (issued, refunded) or an instant (a payment), each in the company's day
  const day = (d: string) => dateText(d, timeZone, { year: false, now: today });
  const status = inv ? statusOf(inv, today) : null;
  const moneyInTitle = inv?.applied.some((a) => adjustedWord(a, inv.number, day))
    ? 'Money in and adjustments'
    : 'Money in';
  // The sheet's parts, in Riverstone's one column.
  const body = (inv: InvoiceSheetData) => {
    const facts = (
      <dl className="inv-facts">
        {inv.issuedOn && (
          <div>
            <dt>Issued</dt>
            <dd>{day(inv.issuedOn)}</dd>
          </div>
        )}
        {inv.dueOn && (
          <div>
            <dt>Due</dt>
            <dd>{dueText(inv, today)!.replace(/^Due /, '')}</dd>
          </div>
        )}
        <div>
          <dt>PO</dt>
          <dd>{inv.poNumber ?? '—'}</dd>
        </div>
      </dl>
    );
    const lines = (
      <div className="table-wrap">
        <table className="t inv-lines" aria-label={`${inv.number}’s lines`}>
          <thead>
            <tr>
              <th>Description</th>
              <th className="num">Qty</th>
              <th className="num">Price</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.description}
                  {/* ServiceTitan's item code and the line's technician (history fix 2) */}
                  {lineTagsText(l) && <span className="muted small"> · {lineTagsText(l)}</span>}
                  {(l.fromEstimate || l.overrideReason) && (
                    <span className="muted small block">
                      {l.fromEstimate ? 'From the estimate' : ''}
                      {l.fromEstimate && l.overrideReason ? ' · ' : ''}
                      {l.overrideReason ?? ''}
                    </span>
                  )}
                </td>
                <td className="num">{qtyText(l.quantity)}</td>
                <td className="num">
                  <Money cents={l.unitPriceCents} />
                </td>
                <td className="num">
                  <Money cents={l.totalCents} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    // what it bills and what was kept, less what was given back (QA-713)
    const after = afterRefunds(inv);
    const refundTax = refundTaxText(inv);
    const totals = (
      <dl className="inv-totals">
        <div>
          <dt>Subtotal</dt>
          <dd>
            <Money cents={inv.subtotalCents} />
          </dd>
        </div>
        {inv.taxes.map((t) => (
          <div key={t.code}>
            <dt>
              {t.code} {rateText(t.rate)} on <Money cents={t.baseCents} />
            </dt>
            <dd>
              <Money cents={t.taxCents} />
            </dd>
          </div>
        ))}
        {inv.taxExempt && (
          <div>
            <dt>{inv.taxExempt}</dt>
            <dd>
              <Money cents={0} />
            </dd>
          </div>
        )}
        <div className="strong">
          <dt>Total</dt>
          <dd>
            <Money cents={inv.totalCents} />
          </dd>
        </div>
        {after.refundedCents > 0 && (
          <>
            <div>
              <dt>
                Refunded, a credit note
                {refundTax && <span className="muted small block">Tax back: {refundTax}</span>}
              </dt>
              <dd>
                <Money cents={-after.refundedCents} />
              </dd>
            </div>
            <div className="strong">
              <dt>Total after refunds</dt>
              <dd>
                <Money cents={after.totalCents} />
              </dd>
            </div>
          </>
        )}
        {inv.writtenOffCents > 0 && (
          <div>
            <dt>Written off</dt>
            <dd>
              <Money cents={inv.writtenOffCents} />
            </dd>
          </div>
        )}
        {/* what credit notes took off it: settled, not paid (history fix 2) */}
        {(inv.adjustedCents ?? 0) > 0 && (
          <div>
            <dt>Adjusted</dt>
            <dd>
              <Money cents={-inv.adjustedCents!} />
            </dd>
          </div>
        )}
        <div>
          <dt>{after.refundedCents > 0 ? 'Paid, less what was given back' : 'Paid'}</dt>
          <dd>
            <Money cents={after.paidCents} />
          </dd>
        </div>
        <div className="strong">
          <dt>Balance</dt>
          <dd>
            <Money cents={inv.balanceCents} />
          </dd>
        </div>
      </dl>
    );
    const moneyIn = (inv.applied.length > 0 || (refunds?.refunds.length ?? 0) > 0) && (
      <section className="job-sect" aria-label={moneyInTitle}>
        <h3>{moneyInTitle}</h3>
        <ul className="rows inv-money">
          {inv.applied.map((a, i) => {
            // a credit note on this invoice names itself and takes its amount off (history fix 2)
            const adjusted = adjustedWord(a, inv.number, day);
            // a payment more than this invoice took says where the rest went (QA-716)
            const rest = restOfPayment(a);
            return (
              <li key={`${a.kind}-${a.at}-${i}`}>
                <span>
                  {adjusted ?? `${day(a.at)} · ${appliedWord(a)}`}
                  {!adjusted && rest && <span className="muted small"> ({rest})</span>}
                </span>
                <Money cents={adjusted ? -a.amountCents : a.amountCents} />
              </li>
            );
          })}
          {refunds?.refunds.map((r) => (
            <li key={r.refundId}>
              <span>
                {day(r.refundedOn)} · Refunded by {methodWord(r.method)}: {r.reason}
              </span>
              <Money cents={-r.amountCents} />
            </li>
          ))}
        </ul>
      </section>
    );
    const depositSect = depositNotes.length > 0 && (
      <section className="job-sect inv-deposits" aria-label="Deposits">
        <h3>Deposits</h3>
        {depositNotes.map((t) => (
          <p key={t} className="small">
            {t}
          </p>
        ))}
      </section>
    );
    // the customer's signature accepting the work, who and when, and the pad (QA-727)
    const signedSect = inv.signature && <SignedForWork signatures={[inv.signature]} timeZone={timeZone} />;
    const saidLine = said && (
      <p className="notice ok" role="status">
        {said}
      </p>
    );
    const waits = draftNote(inv);
    const acts = (
      <div className="job-sect inv-acts">
        {waits && <p className="muted">{waits}</p>}
        {inv.status !== 'draft' && (
          <InvoiceActions
            invoice={inv}
            sendWord={
              inv.status === 'sent' || inv.status === 'partially_paid' ? 'Send again' : 'Send to customer'
            }
            onSent={() => void moved('Sent.')}
          />
        )}
        {inv.cardBeingChecked && (
          <CardBeingChecked
            check={inv.cardBeingChecked}
            timeZone={timeZone}
            canAct={rights.record}
            onDone={(m) => void moved(m)}
          />
        )}
        {inv.balanceCents > 0 &&
          inv.status !== 'void' &&
          (rights.record || (rights.writeOff && inv.status !== 'draft')) && (
            <div className="row">
              {rights.record && !paying && !inv.cardBeingChecked && (
                <button className="btn primary" onClick={() => setPaying(true)}>
                  Record a payment
                </button>
              )}
              {/* only a finalised invoice is written off (invoices.writeOff), never a draft (QA-804) */}
              {rights.writeOff && inv.status !== 'draft' && (
                <button className="btn quiet" onClick={() => setWritingOff(true)}>
                  Write off…
                </button>
              )}
            </div>
          )}
      </div>
    );
    const payingForm = paying && (
      // books' shared form (F-07): this invoice first, then the customer's others oldest first
      <div className="books">
        <RecordPayment
          customerId={inv.customerId}
          customerName={inv.customerName}
          firstInvoiceId={inv.id}
          timeZone={timeZone}
          dates={held.includes('invoices.edit')}
          toast={setSaid}
          onDone={(recorded) => {
            setPaying(false);
            if (recorded) void moved();
          }}
        />
      </div>
    );
    const writeOffSheet = writingOff && (
      <WriteOff
        invoice={inv}
        onClose={() => setWritingOff(false)}
        onDone={(m) => {
          setWritingOff(false);
          void moved(m);
        }}
      />
    );
    return (
      <>
        {facts}
        {lines}
        {totals}
        {moneyIn}
        {depositSect}
        {signedSect}
        {saidLine}
        {acts}
        {payingForm}
        {writeOffSheet}
      </>
    );
  };

  return (
    <dialog
      ref={ref}
      className="sheet job-sheet invoice-sheet"
      aria-labelledby="invoice-sheet-title"
      onClose={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="job-head">
        <div>
          <h2 id="invoice-sheet-title">{inv ? `Invoice ${inv.number}` : 'Invoice'}</h2>
          {inv && (
            <p className="ink2 inv-who">
              <button className="inv-link" onClick={() => onOpenCustomer(inv.customerId)}>
                {inv.customerName}
              </button>
              {inv.jobId && inv.jobNumber && (
                <>
                  {' · '}
                  <button
                    className="inv-link"
                    onClick={() => {
                      ref.current?.close();
                      openJob(inv.jobId!);
                    }}
                  >
                    Job #{inv.jobNumber}
                  </button>
                </>
              )}
              {/* ServiceTitan's while mirroring: sent, paid and written off there (history fix 2) */}
              {inv.imported && mirroring && ' · From ServiceTitan'}
            </p>
          )}
          {mirroring && moneyRights(held).record && <p className="muted small">{MIRROR_LINE}</p>}
        </div>
        <div className="row">
          {status && <span className={`chip say${status.tone ? ` ${status.tone}` : ''}`}>{status.word}</span>}
          <button
            type="button"
            className="btn quiet icon"
            aria-label="Close"
            onClick={() => ref.current?.close()}
          >
            ×
          </button>
        </div>
      </div>

      {error && !inv && <p className="error">{error}</p>}
      {!inv && !error && <Loading error={null} what="the invoice" />}

      {inv && body(inv)}
    </dialog>
  );
}
